import React, { useEffect, useRef, useState } from "react";
import { animateFooterEnter, cleanupFooterAnimations } from "./gsap-footer";
import CompanyInfo from "./CompanyInfo";
import SiteNavigation from "./SiteNavigation";
import ContactInfo from "./ContactInfo";
import LegalLinks from "./LegalLinks";
import AgeDisclaimer from "./AgeDisclaimer";

const Footer = () => {
  const footerRef = useRef<HTMLElement>(null);
  const [currentYear] = useState(new Date().getFullYear());
  const [isInView, setIsInView] = useState(false);
  const [isAnimationReady, setIsAnimationReady] = useState(false);

  useEffect(() => {
    // First, make sure content is visible
    const timer = setTimeout(() => {
      setIsAnimationReady(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isAnimationReady) return;

    let cleanup: (() => void) | undefined;

    // Intersection Observer for better performance
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);

          // Apply entrance animation when footer comes into view
          if (footerRef.current) {
            cleanup = animateFooterEnter({ current: footerRef.current });
          }
        }
      },
      {
        threshold: 0.1,
        rootMargin: "50px 0px",
      }
    );

    if (footerRef.current) {
      observer.observe(footerRef.current);
    }

    // Clean up observer and animations when component unmounts
    return () => {
      observer.disconnect();
      if (cleanup) {
        cleanup();
      }
      cleanupFooterAnimations();
    };
  }, [isInView, isAnimationReady]);

  return (
    <footer
      ref={footerRef}
      className="bg-law-black text-white pt-16 pb-8 relative overflow-hidden"
      style={{
        minHeight: "400px",
        // Ensure footer is visible while animations load
        opacity: isAnimationReady ? 1 : 1,
      }}
    >
      {/* Subtle background gradient for depth */}
      <div className="absolute inset-0 bg-gradient-to-br from-law-black via-gray-900 to-law-black opacity-50"></div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Main Footer Grid - 3 columns */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {/* Company Info */}
          <div
            className={`footer-animated md:col-span-1 ${
              !isAnimationReady ? "opacity-100" : ""
            }`}
            style={{ opacity: !isAnimationReady ? 1 : undefined }}
          >
            <CompanyInfo />
          </div>

          {/* Quick Links */}
          <div
            className={`footer-animated md:col-span-1 ${
              !isAnimationReady ? "opacity-100" : ""
            }`}
            style={{ opacity: !isAnimationReady ? 1 : undefined }}
          >
            <SiteNavigation />
          </div>

          {/* Contact Info */}
          <div
            className={`footer-animated md:col-span-1 ${
              !isAnimationReady ? "opacity-100" : ""
            }`}
            style={{ opacity: !isAnimationReady ? 1 : undefined }}
          >
            <ContactInfo />
          </div>
        </div>

        {/* Animated Divider */}
        <div
          className={`footer-animated relative mb-8 ${
            !isAnimationReady ? "opacity-100" : ""
          }`}
          style={{ opacity: !isAnimationReady ? 1 : undefined }}
        >
          <div className="h-px bg-gradient-to-r from-transparent via-gray-600 to-transparent"></div>
        </div>

        {/* Bottom Section */}
        <div
          className={`flex flex-col md:flex-row justify-between items-center footer-animated ${
            !isAnimationReady ? "opacity-100" : ""
          }`}
          style={{ opacity: !isAnimationReady ? 1 : undefined }}
        >
          <div className="mb-4 md:mb-0 text-center md:text-left">
            <p className="text-gray-400">
              © {currentYear} Street Lawyer Services. All rights reserved.
            </p>
            <p className="text-gray-500 text-xs mt-1">
              Website by{" "}
              <a
                href="https://marketbrewer.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-cannabis-green-300 hover:text-cannabis-green-200 transition-colors duration-300 hover:underline"
              >
                MarketBrewer
              </a>
            </p>
          </div>
          <LegalLinks />
        </div>

        {/* Age Disclaimer with enhanced styling */}
        <div
          className={`mt-8 text-center footer-animated ${
            !isAnimationReady ? "opacity-100" : ""
          }`}
          style={{ opacity: !isAnimationReady ? 1 : undefined }}
        >
          <AgeDisclaimer />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
